/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { IMAGE_UPLOAD_CONFIGURATION } from 'app/shared/components/upload/upload.models';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';

import { ActivatedRoute, Router } from '@angular/router';
import { Constants } from 'app/core/models/constants';
import { ETicketDesignFormModel } from 'app/modules/design/e-ticket-design/e-ticket-design-management/models/e-ticket-design-form.model';
import { eTicketDesignService } from 'app/modules/design/e-ticket-design/e-ticket-design-management/services/e-ticket-design.service';
import { FormMode } from 'app/shared/enumeration/form-mode';
import { setTinyMceTabIndex } from 'app/shared/helpers/tiny-mce-tabindex-helper';
import { ImageUploadErrorType } from 'app/shared/models/image-upload-error-type';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { ETicketBackgroundOption } from '../models/e-ticket-background-option';
import { ETicketLayoutStyle } from '../models/e-ticket-layout-style';
import { StringUtilities } from 'app/shared/helpers/string-helpers';
import { CookieService } from 'app/shared/services/cookie.service';

@Component({
	selector: 'e-ticket-design-form',
	templateUrl: './e-ticket-design-form.component.html',
	styleUrls: ['./e-ticket-design-form.component.scss'],
	standalone: false,
})
export class ETicketDesignFormComponent extends CreateUpdateBaseComponent<ETicketDesignFormModel> implements OnInit {
	readonly imageUploadConfiguration = IMAGE_UPLOAD_CONFIGURATION;
	tinyMceSettings: any;
	tinyMceApiKey = environment.tinyMceApiKey;

	layoutStyleLookupData: any[];
	backgroundOptionLookupData: any[];

	layoutStyleChangedSubscription: Subscription;

	imageValidationMessage: string;
	imageIsValid: boolean;

	constructor(
		protected fb: FormBuilder,
		private service: eTicketDesignService,
		protected asideService: AsideService,
		private route: ActivatedRoute,
		private router: Router,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService, private cookieService: CookieService
	) {
		super(fb, asideService, formHelperService, confirmationDialogService);
	}

	// shortcuts to controls for readability
	get name(): AbstractControl {
		return this.dataForm.controls['name'];
	}
	get layoutStyle(): AbstractControl {
		return this.dataForm.controls['layoutStyle'];
	}
	get content(): AbstractControl {
		return this.dataForm.controls['content'];
	}
	get fadeImageTowardsText(): AbstractControl {
		return this.dataForm.controls['fadeImageTowardsText'];
	}
	get backgroundOption(): AbstractControl {
		return this.dataForm.controls['backgroundOption'];
	}
	get image(): AbstractControl {
		return this.dataForm.controls['image'];
	}

	get imageIsVisible(): boolean {
		return this.dataForm && this.layoutStyle?.value !== ETicketLayoutStyle.FullText && this.layoutStyle?.value != null;
	}
	get layoutStyleIsFullWidthImage(): boolean {
		return this.dataForm && this.layoutStyle?.value === ETicketLayoutStyle.FullWidthImage;
	}
	get layoutStyleIsEitherImageWithTextOption(): boolean {
		return this.dataForm && (this.layoutStyle?.value === ETicketLayoutStyle.ImageAndText || this.layoutStyle?.value === ETicketLayoutStyle.TextAndImage);
	}

	get showETicketText(): boolean {
		return this.dataForm && this.layoutStyle?.value !== ETicketLayoutStyle.FullWidthImage && this.layoutStyle?.value != null;
	}

	get showFadeImage(): boolean {
		return this.dataForm && this.layoutStyle?.value !== ETicketLayoutStyle.FullWidthImage && this.layoutStyle?.value !== ETicketLayoutStyle.FullText && this.layoutStyle?.value != null;
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createFormGroup(): FormGroup<any> {
		return this.fb.group({
			name: ['', [Validators.required, Validators.maxLength(200)]],
			layoutStyle: [''],
			content: [''],
			fadeImageTowardsText: [''],
			backgroundOption: [''],
			image: ['', []],
		});
	}

	get isCreateMode(): boolean {
		return this.formMode == FormMode.Create;
	}
	ngOnInit() {
		this.tinyMceSettings = setTinyMceTabIndex(6, this.cookieService);
		super.ngOnInit();
	}
	mceOnInit() {
		this.content.markAsPristine();
	}

	// overidden from CreateUpdateBaseComponent base class
	protected populateControls(datafrom: FormGroup<any>) {
		this.layoutStyleLookupData = this.formHelperService.createLookupData(this.viewModel.layoutStyleLookupData);
		this.backgroundOptionLookupData = this.formHelperService.createLookupData(this.viewModel.backgroundOptionLookupData);

		this.name.setValue(this.viewModel.name);
		this.content.setValue(this.viewModel.content);
		this.image.setValue(this.viewModel.image);
		this.layoutStyleChangedSubscription = this.layoutStyle.valueChanges.subscribe((value) => this.onLayoutStyleChanged(value));
		this.layoutStyle.setValue(this.viewModel.layoutStyle);
		// set these values after the layoutStyle is set so that the values are set here on the load rather than in the subscription
		this.fadeImageTowardsText.setValue(this.isCreateMode ? false : this.viewModel.fadeImageTowardsText);
		this.backgroundOption.setValue(this.isCreateMode ? ETicketBackgroundOption.Blank : this.viewModel.backgroundOption);
	}

	ngOnDestroy() {
		if (this.layoutStyleChangedSubscription) {
			this.layoutStyleChangedSubscription.unsubscribe();
		}
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<ETicketDesignFormModel> {
		let dataModel: ETicketDesignFormModel;

		await this.service.getEditableItemDetail(id).then((response: ETicketDesignFormModel) => {
			dataModel = response;
		});

		return Promise.resolve(dataModel);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createUpdateModel() {
		//using !imageIsVisible here to ensure the image is updated to null in the API if the image is not visible
		var imageHasChanged = this.viewModel.image !== this.image.value || !this.imageIsVisible;
		return {
			name: this.name.value,
			layoutStyle: this.layoutStyle.value,
			content: this.layoutStyleIsFullWidthImage ? null : this.content.value,
			fadeImageTowardsText: this.fadeImageTowardsText.value,
			backgroundOption: this.backgroundOption.value,
			image: this.imageIsVisible ? this.image.value : null,
			imageHasChanged: imageHasChanged,
			timestamp: this.viewModel ? this.viewModel.timestamp : null,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		let updateResponse: ApiResponseSummary<any>;

		await this.service.updateItem(id, dataToUpdate).then((response: ApiResponseSummary<any>) => {
			updateResponse = response;
		});

		return Promise.resolve(updateResponse);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async doCreate(dataToUpdate: any) {
		await this.service.addNew(dataToUpdate);
	}

	// overidden from CreateUpdateBaseComponent base class
	save() {
		this.image.updateValueAndValidity();
		var imageHasNoErrors = this.image.valid && (this.imageIsValid || this.imageIsValid === null || this.imageIsValid === undefined);
		if (imageHasNoErrors) {
			super.save();
		}
	}

	private onLayoutStyleChanged(value: ETicketLayoutStyle) {
		this.fadeImageTowardsText.setValue(false);
		this.backgroundOption.setValue(ETicketBackgroundOption.Blank);
		switch (value) {
			case ETicketLayoutStyle.FullWidthImage:
				this.image.setValidators([Validators.required]);
				this.image.updateValueAndValidity();
				return;
			case ETicketLayoutStyle.ImageAndText:
			case ETicketLayoutStyle.TextAndImage:
				this.image.setValidators([Validators.required]);
				this.image.updateValueAndValidity();
				this.content.setValidators([Validators.required]);
				this.content.updateValueAndValidity();
				return;
			case ETicketLayoutStyle.FullText:
				this.image.clearValidators();
				this.image.updateValueAndValidity();
				this.content.setValidators([Validators.required]);
				this.content.updateValueAndValidity();
				return;
			default:
				this.image.clearValidators();
				this.image.updateValueAndValidity();
				this.content.clearValidators();
				this.content.updateValueAndValidity();
				break;
		}
	}

	getImageName(id: ETicketLayoutStyle) {
		switch (id) {
			case ETicketLayoutStyle.FullWidthImage:
				return 'eTicket-full-width-image';
			case ETicketLayoutStyle.ImageAndText:
				return 'eTicket-image-and-text';
			case ETicketLayoutStyle.TextAndImage:
				return 'eTicket-text-and-image';
			case ETicketLayoutStyle.FullText:
				return 'eTicket-full-text';
			default:
				break;
		}
	}
	getImageTooltip(id: number) {
		switch (id) {
			case ETicketLayoutStyle.FullWidthImage:
				return 'the uploaded image should be 1424 pixels x 570 pixels';
			case ETicketLayoutStyle.ImageAndText:
			case ETicketLayoutStyle.TextAndImage:
				return 'the uploaded image should be 712 pixels x 570 pixels';
			case ETicketLayoutStyle.FullText:
			default:
				return '';
		}
	}
	setImageValidationMessage(invalidErrorType: ImageUploadErrorType) {
		switch (invalidErrorType) {
			case ImageUploadErrorType.InvalidMimeType:
				this.setImageIsValid(false);
				this.imageValidationMessage = 'eTicket Image must be a jpeg or png file type';
				break;
			case ImageUploadErrorType.FileTooLarge:
				this.setImageIsValid(false);
				this.imageValidationMessage = 'eTicket Image must be less than 10MB in size';
				break;
			default:
				this.setImageIsValid(true);
				this.imageValidationMessage = '';
				break;
		}
	}
	setImageIsValid(isValid: boolean) {
		this.imageIsValid = isValid;
	}

	showImageError() {
		//specifically check for 'this.imageIsValid === false' and not just '!this.imageIsValid' as it could be undefined or null
		return this.image.touched && (this.image.invalid || this.imageIsValid === false);
	}
	imageErrorMessage() {
		if (this.showImageError()) {
			if (this.image.errors?.required) {
				return 'Image is required';
			} else {
				return this.image.errors?.upload ?? this.imageValidationMessage;
			}
		}
		return '';
	}
}
